import {
  Position,
  Cell,
  Board,
  Player,
  GameSession,
  GameSettings,
  Move,
  RoomCode,
  Profile,
  Credentials,
  GameMode,
  PlayerType,
  AIDifficulty,
  GameStatus,
  Mark,
  AuthMethod
} from '../types';

// Position validation
export function isValidPosition(position: Position): boolean {
  return (
    typeof position.row === 'number' &&
    typeof position.col === 'number' &&
    position.row >= 0 &&
    position.row < 3 &&
    position.col >= 0 &&
    position.col < 3 &&
    Number.isInteger(position.row) &&
    Number.isInteger(position.col)
  );
}

// Cell validation
export function isValidCell(cell: Cell): boolean {
  if (!isValidPosition(cell.position)) {
    return false;
  }
  
  // Mark can be null or a valid Mark enum value
  if (cell.mark !== null && !(cell.mark === Mark.X || cell.mark === Mark.O)) {
    return false;
  }
  
  // PlayerId can be null or a non-empty string
  if (cell.playerId !== null && (typeof cell.playerId !== 'string' || cell.playerId.trim() === '')) {
    return false;
  }
  
  return true;
}

// Board validation
export function isValidBoard(board: Board): boolean {
  if (board.size !== 3) {
    return false;
  }
  
  if (!Array.isArray(board.cells) || board.cells.length !== 3) {
    return false;
  }
  
  for (let row = 0; row < 3; row++) {
    if (!Array.isArray(board.cells[row]) || board.cells[row].length !== 3) {
      return false;
    }
    
    for (let col = 0; col < 3; col++) {
      const cell = board.cells[row][col];
      if (!isValidCell(cell)) {
        return false;
      }
      
      // Verify position matches array indices
      if (cell.position.row !== row || cell.position.col !== col) {
        return false;
      }
    }
  }
  
  return true;
}

// Player validation
export function isValidPlayer(player: Player): boolean {
  if (typeof player.id !== 'string' || player.id.trim() === '') {
    return false;
  }
  
  if (typeof player.name !== 'string' || player.name.trim() === '') {
    return false;
  }
  
  if (!(player.type === PlayerType.HUMAN || player.type === PlayerType.AI)) {
    return false;
  }
  
  // AI players must have difficulty, human players should not
  if (player.type === PlayerType.AI) {
    if (!player.difficulty || !(player.difficulty === AIDifficulty.EASY || 
        player.difficulty === AIDifficulty.MEDIUM || player.difficulty === AIDifficulty.HARD)) {
      return false;
    }
  }
  
  if (player.avatar && typeof player.avatar !== 'string') {
    return false;
  }
  
  return true;
}

// Game settings validation
export function isValidGameSettings(settings: GameSettings): boolean {
  if (!(settings.mode === GameMode.SINGLE_PLAYER || settings.mode === GameMode.MULTIPLAYER)) {
    return false;
  }
  
  if (settings.timeLimit !== undefined && (typeof settings.timeLimit !== 'number' || settings.timeLimit <= 0)) {
    return false;
  }
  
  if (typeof settings.theme !== 'string' || settings.theme.trim() === '') {
    return false;
  }
  
  if (typeof settings.soundEnabled !== 'boolean') {
    return false;
  }
  
  if (typeof settings.realTimeMode !== 'boolean') {
    return false;
  }
  
  return true;
}

// Game session validation
export function isValidGameSession(session: GameSession): boolean {
  if (typeof session.id !== 'string' || session.id.trim() === '') {
    return false;
  }
  
  if (!(session.mode === GameMode.SINGLE_PLAYER || session.mode === GameMode.MULTIPLAYER)) {
    return false;
  }
  
  if (!Array.isArray(session.players) || session.players.length < 1 || session.players.length > 2) {
    return false;
  }
  
  for (const player of session.players) {
    if (!isValidPlayer(player)) {
      return false;
    }
  }
  
  if (!isValidBoard(session.board)) {
    return false;
  }
  
  if (typeof session.currentPlayer !== 'string' || session.currentPlayer.trim() === '') {
    return false;
  }
  
  // Current player must be one of the game players
  const playerIds = session.players.map(p => p.id);
  if (playerIds.indexOf(session.currentPlayer) === -1) {
    return false;
  }
  
  if (!(session.status === GameStatus.WAITING || session.status === GameStatus.IN_PROGRESS || 
        session.status === GameStatus.COMPLETED || session.status === GameStatus.ABANDONED)) {
    return false;
  }
  
  if (!(session.createdAt instanceof Date) || isNaN(session.createdAt.getTime())) {
    return false;
  }
  
  if (!isValidGameSettings(session.settings)) {
    return false;
  }
  
  return true;
}

// Move validation
export function isValidMove(move: Move): boolean {
  if (typeof move.gameId !== 'string' || move.gameId.trim() === '') {
    return false;
  }
  
  if (typeof move.playerId !== 'string' || move.playerId.trim() === '') {
    return false;
  }
  
  if (!isValidPosition(move.position)) {
    return false;
  }
  
  if (!(move.timestamp instanceof Date) || isNaN(move.timestamp.getTime())) {
    return false;
  }
  
  return true;
}

// Room code validation
export function isValidRoomCode(roomCode: RoomCode): boolean {
  if (typeof roomCode.code !== 'string') {
    return false;
  }
  
  // Room code should be exactly 6 alphanumeric characters
  const codeRegex = /^[A-Z0-9]{6}$/;
  if (!codeRegex.test(roomCode.code)) {
    return false;
  }
  
  if (!(roomCode.expiresAt instanceof Date) || isNaN(roomCode.expiresAt.getTime())) {
    return false;
  }
  
  // Expiration should be in the future
  if (roomCode.expiresAt <= new Date()) {
    return false;
  }
  
  if (roomCode.gameId && (typeof roomCode.gameId !== 'string' || roomCode.gameId.trim() === '')) {
    return false;
  }
  
  return true;
}

// Profile validation
export function isValidProfile(profile: Profile): boolean {
  if (typeof profile.id !== 'string' || profile.id.trim() === '') {
    return false;
  }
  
  if (typeof profile.name !== 'string' || profile.name.trim() === '') {
    return false;
  }
  
  if (profile.avatar && typeof profile.avatar !== 'string') {
    return false;
  }
  
  if (profile.email && (typeof profile.email !== 'string' || !isValidEmail(profile.email))) {
    return false;
  }
  
  if (!(profile.authMethod === AuthMethod.GOOGLE || profile.authMethod === AuthMethod.FACEBOOK || 
        profile.authMethod === AuthMethod.APPLE || profile.authMethod === AuthMethod.YAHOO || 
        profile.authMethod === AuthMethod.CUSTOM || profile.authMethod === AuthMethod.GUEST)) {
    return false;
  }
  
  if (!(profile.createdAt instanceof Date) || isNaN(profile.createdAt.getTime())) {
    return false;
  }
  
  return true;
}

// Credentials validation
export function isValidCredentials(credentials: Credentials, authMethod: AuthMethod): boolean {
  switch (authMethod) {
    case AuthMethod.CUSTOM:
      return !!(credentials.email && credentials.password && 
               isValidEmail(credentials.email) && 
               isValidPassword(credentials.password));
    
    case AuthMethod.GOOGLE:
    case AuthMethod.FACEBOOK:
    case AuthMethod.APPLE:
    case AuthMethod.YAHOO:
      return !!(credentials.token && typeof credentials.token === 'string' && credentials.token.trim() !== '');
    
    case AuthMethod.GUEST:
      return true; // No credentials needed for guest
    
    default:
      return false;
  }
}

// Email validation helper
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Password validation helper (secure password requirements)
export function isValidPassword(password: string): boolean {
  // At least 8 characters, contains uppercase, lowercase, number, and special character
  // Allow common special characters: @$!%*?&#^()_+-=[]{}|;:,.<>
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{}|;:,.<>])[A-Za-z\d@$!%*?&#^()_+\-=\[\]{}|;:,.<>]{8,}$/;
  return passwordRegex.test(password);
}

// Utility function to create empty board
export function createEmptyBoard(): Board {
  const cells: Cell[][] = [];
  
  for (let row = 0; row < 3; row++) {
    cells[row] = [];
    for (let col = 0; col < 3; col++) {
      cells[row][col] = {
        position: { row, col },
        mark: null,
        playerId: null
      };
    }
  }
  
  return {
    cells,
    size: 3
  };
}

// Utility function to generate unique IDs
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

// Utility function to generate room codes
export function generateRoomCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}